import { alpha, Box, BoxProps, styled, Typography, TypographyProps } from '@mui/material'
import { colorPalette } from '@app/components/ui/containers/AlertContainer/helpers'
import { focusStyle } from '@app/theme'

type SimpleChoiceProps = BoxProps & {
  isFocused?: boolean
  isReviewMode?: boolean
  disabled?: boolean
  isAnswerMasked?: boolean
  isAnswerMaskingEnabled?: boolean
  isStrikethrough?: boolean
  isStrikethroughEnabled?: boolean
}

const STYLED_PROPS = ['isFocused', 'isStrikethrough', 'isStrikethroughEnabled', 'isReviewMode', 'disabled']

export const SimpleChoiceWrapper = styled(Box, {
  shouldForwardProp: prop => !STYLED_PROPS.includes(prop as string),
})<SimpleChoiceProps>(
  ({
    theme,
    isFocused = false,
    isReviewMode = false,
    disabled = false,
    isAnswerMasked = false,
    isAnswerMaskingEnabled = false,
    isStrikethrough = false,
    isStrikethroughEnabled = false,
  }) => ({
    position: 'relative',
    userSelect: 'none',
    width: '100%',
    padding: theme.spacing(3),
    gap: theme.spacing(6),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: isAnswerMaskingEnabled
      ? `${theme.shape.borderRadius * 1.5}px 0px 0px ${theme.shape.borderRadius * 1.5}px`
      : `${theme.shape.borderRadius * 1.5}px`,
    border: `1px solid ${isAnswerMasked ? theme.palette.grey[100] : theme.palette.blue[100]}`,

    ...(!isAnswerMasked && !isStrikethrough && !isStrikethroughEnabled
      ? {
          ...(isFocused && {
            ...focusStyle(theme).border,
          }),
          ...(isReviewMode || disabled
            ? {
                border: `1px solid ${theme.palette.grey[100]}`,
              }
            : {
                cursor: 'pointer',

                '&:hover': {
                  backgroundColor: theme.palette.blue[50],
                  border: `1px solid ${theme.palette.blue[50]}`,

                  [theme.breakpoints.down('mobile')]: {
                    ...focusStyle(theme).border,
                  },
                },

                ':focus-within': {
                  ...focusStyle(theme).border,
                },
              }),

          ...(disabled &&
            !isReviewMode && {
              [`& ${ContentWrapper}`]: {
                color: theme.palette.grey[500],
              },
            }),
        }
      : {
          ...(isStrikethroughEnabled && {
            cursor: 'pointer',
          }),
        }),

    [theme.breakpoints.down('mobile')]: {
      padding: theme.spacing(2),
    },
  }),
)

export const BubbleWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'blankBubble',
})<
  BoxProps & {
    blankBubble?: boolean
  }
>(({ blankBubble = false }) => ({
  ...(blankBubble && {
    visibility: 'hidden',
    // aria-hidden is applied via the component prop to hide from screen readers
  }),
}))

export const ContentWrapper = styled(Box)<{ isStrikethrough?: boolean }>(
  ({ theme, isStrikethrough = false }) => ({
    width: '100%',
    color: theme.palette.grey[800],
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight,
    position: 'relative',

    ...(isStrikethrough && {
      color: alpha(theme.palette.grey[800], 0.6),

      '& > div': {
        opacity: 0.5,
      },
    }),

    '& p': {
      marginBottom: '0 !important',
    },

    // Override Quill editor styles when used for display only
    '& .ql-editor': {
      overflow: 'visible !important', // fix the scroll bar on math equations
      height: 'auto !important',
    },

    // Ensure the container that holds ql-editor also doesn't have overflow
    '& .ql-container': {
      overflow: 'visible !important',
      height: 'auto !important',
    },

    [theme.breakpoints.down('mobile')]: {
      marginLeft: theme.spacing(4.25),
    },
  }),
)

export const PercentageLabel = styled(Typography)<TypographyProps & { correct?: boolean }>(
  ({ theme, correct }) => ({
    fontWeight: 500,
    color: colorPalette(theme, correct ? 'success' : 'warning').color,
    whiteSpace: 'pre',
    marginRight: theme.spacing(4),
  }),
)
